import React, { useState } from "react";
import { Building2 } from "lucide-react";
import api from "../../constants/api";
import ModalOrSheet from "../ui/ModalOrSheet.jsx";
import AppButton from "../ui/AppButton.jsx";
import { Input, Textarea } from "../forms/shared/FormAtoms.jsx";
import { successButtonLoadingClassName } from "../../design/tokens.js";

const LABEL_CLS = "text-[11px] uppercase tracking-wider text-muted mb-0.5 font-medium";
const FORM_ID = "create-objekt-form";

export default function CreateObjektModal({ kundeId, onCreated, onClose }) {
  const [f, setF] = useState({
    name: "",
    adresse: "",
    plz: "",
    ort: "",
    kontakt_vor_ort_name: "",
    kontakt_vor_ort_tel: "",
    kontakt_vor_ort_email: "",
    zugang_info: "",
    notizen: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!f.name.trim()) {
      setError("Name ist erforderlich");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/objekte", {
        kunde_id: kundeId,
        name: f.name.trim(),
        adresse: f.adresse.trim() || null,
        plz: f.plz.trim() || null,
        ort: f.ort.trim() || null,
        kontakt_vor_ort_name: f.kontakt_vor_ort_name.trim() || null,
        kontakt_vor_ort_tel: f.kontakt_vor_ort_tel.trim() || null,
        kontakt_vor_ort_email: f.kontakt_vor_ort_email.trim() || null,
        zugang_info: f.zugang_info.trim() || null,
        notizen: f.notizen.trim() || null,
      });
      if (data?.ok && data?.id) onCreated?.(data.id, f.name.trim());
    } catch (err) {
      setError(err?.response?.data?.error || "Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOrSheet
      open
      onClose={onClose}
      title="Neues Objekt erstellen"
      maxWidth="lg"
      footer={
        <div className="flex flex-col-reverse max-md:gap-2 sm:flex-row sm:justify-end gap-2 w-full">
          <AppButton type="button" variant="secondary" onClick={onClose} className="max-md:min-h-[44px]">
            Abbrechen
          </AppButton>
          <AppButton
            type="submit"
            form={FORM_ID}
            variant="success"
            disabled={saving || !f.name.trim()}
            className={`max-md:min-h-[44px] ${saving ? successButtonLoadingClassName : ""}`}
          >
            {saving ? "Erstellen…" : "Objekt erstellen"}
          </AppButton>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={handleSave} className="space-y-3">
        <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2 text-primary">
          <Building2 className="w-5 h-5 text-blue-400 shrink-0" aria-hidden />
          Objekt / Liegenschaft
        </h3>
        <div className="space-y-2.5">
          <div>
            <div className={LABEL_CLS}>Name *</div>
            <Input
              aria-label="Objektname"
              value={f.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="z. B. Hauptgebäude"
              enterKeyHint="next"
            />
          </div>
          <div>
            <div className={LABEL_CLS}>Adresse</div>
            <Input
              aria-label="Objektadresse"
              value={f.adresse}
              onChange={(e) => set("adresse", e.target.value)}
              placeholder="Strasse + Nr."
              enterKeyHint="next"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <div className={LABEL_CLS}>PLZ</div>
              <Input aria-label="Objekt PLZ" value={f.plz} onChange={(e) => set("plz", e.target.value)} enterKeyHint="next" />
            </div>
            <div className="sm:col-span-2">
              <div className={LABEL_CLS}>Ort</div>
              <Input aria-label="Objekt Ort" value={f.ort} onChange={(e) => set("ort", e.target.value)} enterKeyHint="next" />
            </div>
          </div>
          <div>
            <div className={LABEL_CLS}>Kontakt vor Ort</div>
            <Input
              aria-label="Kontakt vor Ort"
              value={f.kontakt_vor_ort_name}
              onChange={(e) => set("kontakt_vor_ort_name", e.target.value)}
              placeholder="Ansprechperson"
              enterKeyHint="next"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className={LABEL_CLS}>Telefon</div>
              <Input
                aria-label="Kontakttelefon vor Ort"
                value={f.kontakt_vor_ort_tel}
                onChange={(e) => set("kontakt_vor_ort_tel", e.target.value)}
                type="tel"
                enterKeyHint="next"
              />
            </div>
            <div>
              <div className={LABEL_CLS}>E-Mail</div>
              <Input
                aria-label="Kontakt E-Mail vor Ort"
                value={f.kontakt_vor_ort_email}
                onChange={(e) => set("kontakt_vor_ort_email", e.target.value)}
                type="email"
                enterKeyHint="next"
              />
            </div>
          </div>
          <div>
            <div className={LABEL_CLS}>Zugangsinfo</div>
            <Textarea
              aria-label="Zugangsinfo"
              rows={2}
              value={f.zugang_info}
              onChange={(e) => set("zugang_info", e.target.value)}
              placeholder="Schlüssel, Code, Besonderheiten…"
            />
          </div>
          <div>
            <div className={LABEL_CLS}>Notizen</div>
            <Textarea
              aria-label="Objekt Notizen"
              rows={2}
              value={f.notizen}
              onChange={(e) => set("notizen", e.target.value)}
            />
          </div>
        </div>
        {error ? <div className="text-sm text-red-400">{error}</div> : null}
      </form>
    </ModalOrSheet>
  );
}
